import React, { useEffect } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import Refresh from '@mui/icons-material/Refresh';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import { Divider } from '@mui/material';

import { SelectedItemIdentifier } from '../../../../library/selection/types';
import { useAppMonitor } from '../../../../monitor';
import {
  regenerateAllTrussEnvelopesLinkedToComponentDesign,
  selectAllTrussEnvelopesLinkedToComponentDesign,
} from '../../../../store/actions/layout/trussEnvelopeActions';
import { loadAdditionalComponentDesignDataWithSpinner } from '../../../../store/actions/loadActions';
import { handleStandardSceneItemEditPropertiesAction } from '../../../../store/actions/sceneInteractionActions';
import { selectItem } from '../../../../store/actions/selectActions';
import { createGetAnalysisHistoryForComponentDesign } from '../../../../store/selectors/getAnalysisHistoryForActiveComponentDesignSelector';
import { getCommittedComponentDesigns } from '../../../../store/selectors/getComponentDesignsSelectors';
import { leftSidebarSet } from '../../../../store/slices/panelDataSlice';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { SceneMenuContent } from '../common/SceneMenuContent';
import { SceneMenuItem } from '../common/SceneMenuItem';
import { DeleteMenuItem } from '../shared-menu-items/DeleteMenuItem';
import { LockOptionsSubMenu } from '../shared-menu-items/LockOptionsSubMenu';
import { SettingsSubMenu } from '../shared-menu-items/SettingsSubMenu';

// IMPORTANT NOTE -- unlike most other context menus, a component design menu can be open for a component design that is
//   not currently selected. Ensure you select the component design if appropriate when menu items are clicked.

export function ComponentDesignMenu(props: { guid: string; matchingSelectedItems: SelectedItemIdentifier[] }) {
  const monitor = useAppMonitor();
  const dispatch = useAppDispatch();

  const componentDesign = useAppSelector((state) =>
    getCommittedComponentDesigns(state).find((design) => design.guid === props.guid),
  );

  const otherComponentsTargeted = props.matchingSelectedItems.filter((item) => item.guid !== props.guid).length > 0;

  const fullyLoaded = useAppSelector((state) => state.componentDesigns.loadedState[props.guid]?.fullyLoaded);

  const linked = useAppSelector(
    ({ layout: { trussEnvelopes } }) =>
      Object.values(trussEnvelopes).filter(({ componentDesignGuid }) => componentDesignGuid === props.guid).length > 0,
  );

  const history = useAppSelector(createGetAnalysisHistoryForComponentDesign(props.guid));

  useEffect(() => {
    if (props.guid != null && !fullyLoaded) dispatch(loadAdditionalComponentDesignDataWithSpinner(props.guid));
  }, [dispatch, props.guid, fullyLoaded]);

  if (componentDesign == null) throw new Error('Unable to find component design with active menu.');
  if (!fullyLoaded) return null;

  if (otherComponentsTargeted) {
    return (
      <SceneMenuContent>
        <DeleteMenuItem
          type="componentDesign"
          item={[componentDesign, ...props.matchingSelectedItems]}
          userVisibleTerm="Selected Components"
        />
      </SceneMenuContent>
    );
  }

  return (
    <SceneMenuContent>
      <SceneMenuItem
        text="Edit Properties"
        icon={<EditIcon />}
        onClick={async () => {
          await dispatch(handleStandardSceneItemEditPropertiesAction('componentDesign', componentDesign));
          monitor.recordInteraction('Component Design => Edit Properties menu item clicked');
        }}
      />

      {linked ? (
        <SceneMenuItem
          icon={<SelectAllIcon />}
          text="Select Linked Envelopes"
          onClick={() => {
            dispatch(selectAllTrussEnvelopesLinkedToComponentDesign(props.guid));
            monitor.recordInteraction('Component Design => Select Linked Envelopes menu item clicked');
          }}
        />
      ) : null}

      {history !== null && history.length > 0 && (
        <SceneMenuItem
          text="Analysis and History"
          icon={<HistoryIcon />}
          onClick={async () => {
            await dispatch(selectItem(componentDesign, 'componentDesign'));
            dispatch(leftSidebarSet('analysisHistory'));
            monitor.recordInteraction('Component Design => Analysis and History menu item clicked');
          }}
        />
      )}

      <Divider />

      <LockOptionsSubMenu type="componentDesign" guid={props.guid} />

      <SettingsSubMenu userVisibleTerm="Truss" entry={{ type: 'componentDesign', item: componentDesign }} />

      <Divider />

      {linked ? (
        <SceneMenuItem
          icon={<Refresh />}
          text="Regenerate Component"
          onClick={async () => {
            monitor.recordInteraction('Component Design => Regenerate Component menu item clicked');
            await dispatch(regenerateAllTrussEnvelopesLinkedToComponentDesign(props.guid));
          }}
        />
      ) : null}

      <DeleteMenuItem type="componentDesign" item={componentDesign} userVisibleTerm="Component" />
    </SceneMenuContent>
  );
}
